import * as React from 'react';
import { Body1AccentedTypography, Button, CaptionAccentTypography, CaptionLinkTypography, Separator } from 'ui';
import type { QueryParamsErrorCodesEnum } from '../../../utils/types/eunms';
import { useLoginForm } from './useLoginForm';
import type { LoginFormValues } from './useLoginForm';
import { Banner } from '@components/atoms';
import { BaseInput, BrandHeader, LinkButton, PasswordInput } from '@components/molecules';
import type { BrandLogo } from '@components/molecules/brand-header';
import { TermsOfServiceLink } from '@components/molecules/terms-of-service-link';
import { SSOActionBar } from '@components/organisms/sso-action-bar';
import type { ResponseResult } from '@utils';
import { WithControl } from '@utils';

export type LoginLabels = {
	email: string;
	password: string;
	forgotPassword?: string;
	submit: string;
	linkText: string;
	linkButton: string;
	title: string;
	subtitle?: string;
	separator: string;
	tosText: string;
	tosButton: string;
	incorrectEmailOrPasswordError: string;
	errors?: {
		[QueryParamsErrorCodesEnum.UNAUTHORIZED]: string;
	};
};

type Props = {
	labels: LoginLabels;
	logo?: BrandLogo;
	tosLink?: string;
	handleNavigateToForgotPassword: () => void;
	onSubmit: (data: LoginFormValues) => Promise<ResponseResult<any>>;
	handleNavigateToLink?: () => void;
	externalError?: string;
	onSSOSuccess: () => void;
};

export const LoginForm: React.FC<Props> = ({
	labels,
	logo,
	handleNavigateToLink,
	onSubmit,
	tosLink,
	handleNavigateToForgotPassword,
	externalError,
	onSSOSuccess,
}) => {
	const { control, isValid, isSubmitted, isSubmitting, handleSubmit, error, setError } = useLoginForm({ onSubmit });

	const ControlledPasswordInput = WithControl(PasswordInput, control);
	const ControlledEmailInput = WithControl(BaseInput, control);

	const hasError: boolean = Boolean(error || externalError);
	const showError: boolean = Boolean(hasError && (externalError || isSubmitted));

	const onNavigateToForgetPassword = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		handleNavigateToForgotPassword();
	};

	const handleSSOError = (error: Error) => {
		if (!error) {
			return;
		}

		setError(error.message);
	};

	return (
		<div className="flex flex-col gap-[2rem]" data-testid="auth-login-form">
			<BrandHeader title={labels.title} subtitle={labels.subtitle} logo={logo} />
			<SSOActionBar onSuccess={onSSOSuccess} onError={handleSSOError} />

			<div className="flex items-center gap-2 whitespace-nowrap">
				<Separator />
				<CaptionAccentTypography className="text-primary" truncate={false}>
					{labels.separator}
				</CaptionAccentTypography>
				<Separator />
			</div>
			<form onSubmit={handleSubmit}>
				<div className="flex flex-col gap-[1.6rem] pb-[1.2rem]">
					{showError && <Banner title={error ?? (externalError as string)} variant="error" />}
					<ControlledEmailInput label={labels.email} name="email" type="email" data-aid="email-input" />
					<div className="align-left flex flex-col gap-[0.5rem]">
						<ControlledPasswordInput label={labels.password} name="password" />
						<CaptionLinkTypography
							className="text-primary hover:cursor-pointer"
							onClick={onNavigateToForgetPassword}
							data-testid="auth-forgot-password-link"
						>
							{labels.forgotPassword}
						</CaptionLinkTypography>
					</div>
				</div>
				<div className="flex flex-col gap-[0.75rem]">
					{tosLink && <TermsOfServiceLink link={tosLink} text={labels.tosText} linkText={labels.tosButton} />}
					<Button
						variant="primary"
						className="rounded-2xl bg-brandHeader"
						type="submit"
						disabled={!isValid}
						isLoading={isSubmitting}
						data-testid="auth-login-submit"
					>
						<Body1AccentedTypography>{labels.submit}</Body1AccentedTypography>
					</Button>
					{handleNavigateToLink && (
						<LinkButton linkText={labels.linkText} buttonText={labels.linkButton} onClick={handleNavigateToLink} />
					)}
				</div>
			</form>
		</div>
	);
};
